
import { useState } from 'react';

interface PageItem {
  id: string;
  name: string;
  path: string;
  type: 'page' | 'component';
  status: 'published' | 'draft';
  modified: string;
  size: string;
}

const PageManager = () => {
  const [pages] = useState<PageItem[]>([
    { id: '1', name: 'Home', path: 'src/pages/home/page.tsx', type: 'page', status: 'published', modified: '2024-01-15', size: '4.2 KB' },
    { id: '2', name: 'Dashboard', path: 'src/pages/dashboard/page.tsx', type: 'page', status: 'published', modified: '2024-01-14', size: '8.7 KB' },
    { id: '3', name: 'Projects Overview', path: 'src/pages/projects/overview/page.tsx', type: 'page', status: 'published', modified: '2024-01-13', size: '6.3 KB' },
    { id: '4', name: 'Calendar', path: 'src/pages/calendar/page.tsx', type: 'page', status: 'published', modified: '2024-01-12', size: '5.1 KB' },
    { id: '5', name: 'Sidebar', path: 'src/pages/dashboard/components/Sidebar.tsx', type: 'component', status: 'published', modified: '2024-01-11', size: '3.8 KB' },
    { id: '6', name: 'Dashboard Header', path: 'src/pages/dashboard/components/DashboardHeader.tsx', type: 'component', status: 'published', modified: '2024-01-10', size: '2.9 KB' }
  ]);

  const [filterType, setFilterType] = useState<'all' | 'page' | 'component'>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'published' | 'draft'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPage, setSelectedPage] = useState<PageItem | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editContent, setEditContent] = useState('');

  const filteredPages = pages.filter(page => {
    const matchesType = filterType === 'all' || page.type === filterType;
    const matchesStatus = filterStatus === 'all' || page.status === filterStatus;
    const matchesSearch = page.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         page.path.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesType && matchesStatus && matchesSearch;
  });

  const handleEdit = (page: PageItem) => {
    setSelectedPage(page);
    setEditContent(`// ${page.path}\n// This is a placeholder for the actual file content\n\nimport { useState } from 'react';\n\nconst ${page.name.replace(/\s+/g, '')} = () => {\n  return (\n    <div className="container mx-auto p-6">\n      <h1 className="text-2xl font-bold">${page.name}</h1>\n      <p>Content for ${page.name}</p>\n    </div>\n  );\n};\n\nexport default ${page.name.replace(/\s+/g, '')};`);
    setShowEditModal(true);
  };

  const handleSave = () => {
    console.log('Saving changes to:', selectedPage?.path);
    console.log('Content:', editContent);
    alert(`Changes saved successfully to ${selectedPage?.name}!\n\nNote: In a production environment, this would update the actual file.`);
    setShowEditModal(false);
  };

  const handlePreview = (page: PageItem) => {
    const previewPath = page.path.replace('src/pages/', '/').replace('/page.tsx', '');
    console.log('Opening preview for:', previewPath);
    alert(`Preview functionality would open: ${previewPath}\n\nNote: In a production environment, this would open the page in a new tab.`);
  };

  const handleDelete = (page: PageItem) => {
    if (confirm(`Are you sure you want to delete "${page.name}"?\n\nThis action cannot be undone.`)) {
      console.log('Deleting page:', page.path);
      alert(`"${page.name}" has been deleted.\n\nNote: In a production environment, this would remove the actual file.`);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Page Manager</h2>
        <p className="text-gray-600">Manage and edit all website pages and components</p>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="flex items-center space-x-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mr-2">Type:</label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value as any)}
                className="px-3 py-1 pr-8 border border-gray-300 rounded text-sm"
              >
                <option value="all">All Types</option>
                <option value="page">Pages</option>
                <option value="component">Components</option>
              </select>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mr-2">Status:</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as any)}
                className="px-3 py-1 pr-8 border border-gray-300 rounded text-sm"
              >
                <option value="all">All Status</option>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
              </select>
            </div>
          </div>

          <div className="relative">
            <input
              type="text"
              placeholder="Search pages..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full md:w-64 pl-8 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
            <i className="ri-search-line absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"></i>
          </div>
        </div>
      </div>

      {/* Pages List */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-6 py-3 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              Pages & Components ({filteredPages.length})
            </h3>
          </div>
        </div>

        <div className="divide-y divide-gray-200">
          {filteredPages.map((page) => (
            <div key={page.id} className="px-6 py-4 hover:bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center flex-1">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-4 ${
                    page.type === 'page' ? 'bg-blue-100' : 'bg-purple-100'
                  }`}>
                    <i className={`${
                      page.type === 'page' ? 'ri-file-text-line text-blue-600' : 'ri-puzzle-line text-purple-600'
                    } text-lg`}></i>
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center">
                      <h4 className="text-sm font-medium text-gray-900">{page.name}</h4>
                      <span className={`ml-3 px-2 py-0.5 text-xs font-medium rounded-full ${
                        page.status === 'published' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {page.status}
                      </span>
                      <span className="ml-3 px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">
                        {page.type}
                      </span>
                    </div>
                    <div className="flex items-center mt-1 text-xs text-gray-500">
                      <span>{page.path}</span>
                      <span className="mx-2">•</span>
                      <span>{page.size}</span>
                      <span className="mx-2">•</span>
                      <span>Modified: {page.modified}</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2 ml-4">
                  <button
                    onClick={() => handlePreview(page)}
                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                    title="Preview"
                  >
                    <i className="ri-eye-line"></i>
                  </button>
                  <button
                    onClick={() => handleEdit(page)}
                    className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg"
                    title="Edit"
                  >
                    <i className="ri-edit-line"></i>
                  </button>
                  <button
                    onClick={() => handleDelete(page)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                    title="Delete"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && selectedPage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Edit: {selectedPage.name}</h3>
                <p className="text-sm text-gray-500">{selectedPage.path}</p>
              </div>
              <button
                onClick={() => setShowEditModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="flex-1 overflow-hidden">
              <textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                className="w-full h-full p-6 font-mono text-sm bg-gray-900 text-green-400 resize-none focus:outline-none"
                spellCheck={false}
              />
            </div>

            <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
              <div className="text-sm text-gray-600">
                <i className="ri-information-line mr-1"></i>
                Changes will be saved to: {selectedPage.path}
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 whitespace-nowrap"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 whitespace-nowrap"
                >
                  <i className="ri-save-line mr-2"></i>
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PageManager;
